"use client";

import { getAppStoreUrls } from "@/lib/app-store-urls";
import { useLanguage } from "@/lib/useLanguage";

const APPLE_ICON = (
  <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19z" />
  </svg>
);

const PLAY_ICON = (
  <svg className="w-5 h-5 shrink-0" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
    <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.8 8.99l-2.302 2.302-8.634-8.635z" />
  </svg>
);

type AppStoreDownloadButtonsProps = {
  className?: string;
  iosLabel?: string;
  androidLabel?: string;
  iosHref?: string;
  androidHref?: string;
  onIosClick?: () => void;
  onAndroidClick?: () => void;
};

export function AppStoreDownloadButtons({
  className = "",
  iosLabel,
  androidLabel,
  iosHref: iosHrefProp,
  androidHref: androidHrefProp,
  onIosClick,
  onAndroidClick,
}: AppStoreDownloadButtonsProps) {
  const { t } = useLanguage();
  const d = t.download;
  const resolvedIosLabel = iosLabel ?? d?.appStore ?? "App Store";
  const resolvedAndroidLabel = androidLabel ?? d?.playStore ?? "Google Play";
  const { ios: defaultIosUrl, android: defaultAndroidUrl } = getAppStoreUrls();
  const iosUrl = iosHrefProp ?? defaultIosUrl;
  const androidUrl = androidHrefProp ?? defaultAndroidUrl;

  return (
    <div className={`grid grid-cols-1 sm:grid-cols-2 gap-2 w-full ${className}`}>
      <a
        href={iosUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onIosClick}
        className="inline-flex items-center justify-center gap-2 w-full min-w-0 min-h-16 px-6 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:hover:bg-white dark:text-slate-900 font-semibold text-base leading-snug shadow-md shadow-black/15 transition-all focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
        aria-label={resolvedIosLabel}
      >
        {APPLE_ICON}
        {resolvedIosLabel}
      </a>
      <a
        href={androidUrl}
        target="_blank"
        rel="noopener noreferrer"
        onClick={onAndroidClick}
        className="inline-flex items-center justify-center gap-2 w-full min-w-0 min-h-16 px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-base leading-snug shadow-md shadow-emerald-900/25 transition-all focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-slate-950"
        aria-label={resolvedAndroidLabel}
      >
        {PLAY_ICON}
        {resolvedAndroidLabel}
      </a>
    </div>
  );
}
